import classNames from 'classnames';
import * as _ from 'lodash-es';
import React, { createContext, useContext } from 'react';
// eslint-disable-next-line no-restricted-imports
import { NavLink as RouterNavLink, useLocation } from 'react-router';
// eslint-disable-next-line no-restricted-imports
import type { Link as RouterLink, To } from 'react-router';
import useIsPublicView, { isPublicViewPath } from '../useIsPublicView';

export const LinkForceNewTabContext = createContext<boolean>(false);

type SearchLocation = { search?: string } | string;

const getSearch = (location: SearchLocation): string => {
  if (typeof location === 'string') {
    return new URL(location, window.location.origin).search || '';
  }

  return location.search || '';
};

const isSearchEqual = (
  loc1: SearchLocation = '',
  loc2: SearchLocation = '',
): boolean => {
  const search1 = getSearch(loc1);
  const search2 = getSearch(loc2);

  if (search1 === search2) return true;

  const toSortedArray = (search: string) =>
    search.substring(1).split('&').toSorted();

  return _.isEqual(toSortedArray(search1), toSortedArray(search2));
};

const getPathname = (to: To) => {
  try {
    return typeof to === 'string'
      ? new URL(to, window.location.href).pathname
      : to.pathname;
  } catch (err) {
    return null;
  }
};

const useShouldReloadDocument = (to: To) => {
  const isPublicView = useIsPublicView();

  const pathname = getPathname(to);
  return !!pathname && isPublicViewPath(pathname) !== isPublicView;
};

const desktopShouldOpenInBrowser = (to: To) => {
  if (!window.desktop) return false;

  const pathname = getPathname(to);

  if (window.desktop.isHomeTab && pathname !== '/') {
    return true;
  }
  if (
    pathname &&
    window.desktop.constants?.BROWSER_ONLY_PATH_PREFIXES?.some((prefix) =>
      pathname.startsWith(prefix),
    )
  ) {
    return true;
  }

  return false;
};

export const LinkStateContext =
  createContext<React.ComponentProps<typeof Link>['state']>(undefined);

export interface LinkProps
  extends Omit<React.ComponentProps<typeof RouterLink>, 'rel' | 'target'> {
  className?: string;
  activeClassName?: string;
  onlyActiveOnIndex?: boolean;
  onlyActiveOnSearchMatch?: boolean;
  newTab?: boolean | 'ugc';
}

const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      className,
      onlyActiveOnSearchMatch,
      onlyActiveOnIndex,
      activeClassName,
      newTab,
      to,
      ...props
    },
    ref,
  ) => {
    const location = useLocation();
    const shouldReloadDocument = useShouldReloadDocument(to);
    const shouldForceNewTab =
      useContext(LinkForceNewTabContext) || desktopShouldOpenInBrowser(to);
    const linkState = useContext(LinkStateContext);

    return (
      <RouterNavLink
        ref={ref}
        className={(navState) => {
          const isActive =
            navState.isActive &&
            (!onlyActiveOnSearchMatch || isSearchEqual(location, to));

          return (
            classNames(
              className,
              activeClassName && { [activeClassName]: isActive },
            ) || undefined
          );
        }}
        end={onlyActiveOnIndex}
        state={_.merge(linkState, props.state)}
        {...props}
        to={to}
        reloadDocument={props.reloadDocument || shouldReloadDocument}
        target={newTab || shouldForceNewTab ? '_blank' : undefined}
        {...(newTab
          ? { rel: newTab === 'ugc' ? 'nofollow noreferrer ugc' : 'noreferrer' }
          : {})}
      />
    );
  },
);

export default Link;
